//@ts-nocheck
import { useState, useEffect } from 'react'
import {
  flattenDeep,
  get,
  remove,
  isEqual,
  castArray,
  min,
  max,
} from 'lodash-es'
import { Table } from 'semantic-ui-react'
import { useTranslation } from 'react-i18next'
import useResizeAware from 'react-resize-aware'
import { useApolloClient } from '@apollo/client'

import { usePermissionCheck } from '@/hooks'
import useTableViewStore from '@/stores/useTableViewStore'
import { FlexRow, Icon, Text, Button } from '@/ui'
import { BulkMenu, Search } from '@/ui'
import type { BulkItemProps } from '@/ui/BulkMenu'
import TableHeader from './TableHeader'
import TableRows from './TableRows'
import {
  GlobalStyle,
  TableWrapper,
  TableInnerWrapper,
  StyledTable,
  FilterMode,
  FilterModeSelection,
  TotalCountWrapper,
} from './Styles'
import MoreLoader from './MoreLoader'

import { TFunction } from 'i18next'

// Standard columns titles are passed as functions, to make sure the translation function is always evaluated on re-render
export const renderTitle = (title: string | (() => string), t: TFunction) =>
  typeof title === 'string' ? title : t(title())

export const Total = ({
  loading,
  total,
  totalCountText,
  customDataCount,
  data,
  t,
}: {
  loading?: boolean
  total: number
  totalCountText?: string
  customDataCount?: string | number
  data?: any[]
  t: TFunction
}) => {
  if (loading) {
    return <TotalCountWrapper>...</TotalCountWrapper>
  }

  return (
    <TotalCountWrapper>
      {totalCountText ? `${totalCountText} ` : null}
      {t('Showing')} {customDataCount || data?.length} {t('out of')} {total}.
    </TotalCountWrapper>
  )
}

const tableOffsetInterval = 300

interface TableContentProps {
  fixed: boolean
  basic: boolean
  layout: string
  isMobile: boolean
  Header: any
  error: boolean
  columns: any
  data: any
  clickable: boolean
  search: boolean
  checked: any
  segmentLoading: boolean
  loadingMore: boolean
  onCheck: any
  onCheckAll: any
  t: any
  fallbackText: string
}

interface ComposedTableProps {
  addFilter?: any
  basic?: boolean
  bulk?: (Omit<BulkItemProps, 'onClick'> & {
    onClick: (ids: string[]) => void
  })[]
  check?: (values: string[]) => void
  checked?: any[]
  clickable?: (row: any) => void
  columns: any[]
  customDataCount?: any
  data?: any[]
  filters?: any
  fixed?: boolean
  graphileFilters?: any
  loadingTable?: boolean
  loadingMore?: boolean
  moveColumn?: any
  search?: string
  searchable?: boolean
  searchTable?: any
  sortDirection?: string
  sortKey?: any[] | string
  sortTable?: any
  stickyHeader?: boolean
  stickyHeaderNoOffset?: boolean
  style?: any
  tableName?: string
  removeFilter?: any
  invertFilter?: any
  toggleFilterMode?: any
  matchAllColumns?: boolean
  toggleMultiColumnFilterMode?: any
  removeAllFilters?: any
  total?: any
  showTotal?: boolean
  totalCountText?: string
  error?: boolean
  layout?: 'auto' | 'fixed'
  fallbackText?: string
  clearChecked?: any
  clearCheckedOnDismount?: boolean
  forceCheckbox?: boolean
}

const TableContent = ({
  fixed,
  basic,
  layout,
  isMobile,
  Header,
  error,
  columns,
  data,
  clickable,
  search,
  checked,
  loadingMore,
  segmentLoading,
  onCheck,
  onCheckAll,
  t,
  fallbackText,
}: TableContentProps) => {
  const [tableOffset, setTableOffset] = useState(0)
  const [tableWrapperResizeListener, tableWrapperSizes] = useResizeAware()
  const [tableResizeListener, tableSizes] = useResizeAware()

  useEffect(() => {
    if (Math.abs(tableWrapperSizes.width - tableSizes.width) < 15) {
      setTableOffset(0)
    }
  }, [tableWrapperSizes.width, tableSizes.width])
  return (
    <div style={{ position: 'relative' }}>
      {Math.abs(tableWrapperSizes.width - tableSizes.width) > 15 && (
        <div style={{ position: 'absolute', right: '0', top: '-30px' }}>
          <Icon
            button
            size="large"
            disabled={tableOffset === 0}
            name="icon-arrowcircle-left"
            onClick={() =>
              setTableOffset(min([0, tableOffset + tableOffsetInterval]))
            }
          />
          <Icon
            button
            size="large"
            disabled={
              tableWrapperSizes.width - tableOffset === tableSizes.width
            }
            name="icon-arrowcircle-right"
            onClick={() =>
              setTableOffset(
                max([
                  tableOffset - tableOffsetInterval,
                  tableWrapperSizes.width - tableSizes.width,
                ]),
              )
            }
          />
        </div>
      )}

      <TableWrapper loading={segmentLoading} fixed={fixed}>
        {tableWrapperResizeListener}
        <TableInnerWrapper>
          {tableResizeListener}
          <StyledTable
            basic={basic}
            layout={layout}
            celled
            padded
            style={{
              transform: `translateX(${tableOffset}px)`,
            }}>
            {!isMobile && Header}
            <Table.Body>
              {error ? (
                <Table.Row>
                  <Table.Cell colSpan={columns.length} textAlign="center">
                    {t('Request failed. Try again.')}
                  </Table.Cell>
                </Table.Row>
              ) : (
                <TableRows
                  columns={columns}
                  data={data}
                  clickable={clickable}
                  search={search}
                  checked={checked}
                  loading={segmentLoading}
                  onCheck={onCheck}
                  onCheckAll={onCheckAll}
                  t={t}
                  isMobile={isMobile}
                  fallbackText={fallbackText}
                />
              )}
              {loadingMore && <MoreLoader columns={columns} />}
            </Table.Body>
          </StyledTable>
        </TableInnerWrapper>
        {fixed && (
          <StyledTable fixed basic={basic} celled padded>
            {Header}
          </StyledTable>
        )}
      </TableWrapper>
    </div>
  )
}

const ComposedTable = ({
  addFilter,
  basic,
  bulk,
  check,
  checked = [],
  clickable,
  columns = [],
  customDataCount,
  data = [],
  filters,
  fixed = false,
  graphileFilters,
  loadingTable = false,
  loadingMore,
  moveColumn,
  search = '',
  searchable,
  searchTable,
  sortDirection = 'asc',
  sortKey = '',
  sortTable,
  stickyHeader = true,
  stickyHeaderNoOffset,
  style,
  tableName,
  removeFilter,
  invertFilter,
  toggleFilterMode,
  matchAllColumns,
  toggleMultiColumnFilterMode,
  removeAllFilters,
  total,
  showTotal = true,
  totalCountText = '',
  error,
  layout = 'auto',
  fallbackText,
  clearChecked,
  clearCheckedOnDismount = false,
  forceCheckbox = false,
}: ComposedTableProps) => {
  const { t } = useTranslation()
  const { hasPermissions } = usePermissionCheck()

  const client = useApolloClient()

  const [pushTableName, popTableName] = useTableViewStore(state => [
    state.pushTableName,
    state.popTableName,
  ])

  useEffect(() => {
    async function updateCurrentTableName(unmount = false) {
      if (unmount) {
        popTableName()
      } else {
        pushTableName(tableName)
      }
    }

    if (!tableName) {
      return
    }

    updateCurrentTableName()

    return () => {
      updateCurrentTableName(true)
    }
  }, [client, tableName])

  useEffect(() => {
    return () => {
      if (clearCheckedOnDismount) {
        clearChecked()
      }
    }
  }, [clearChecked, clearCheckedOnDismount])

  const [isMobile, setIsMobile] = useState(false)

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 991 && !isMobile) {
        setIsMobile(true)
      }
      if (window.innerWidth > 991 && isMobile) {
        setIsMobile(false)
      }
    }

    window.removeEventListener('resize', handleResize)
    window.addEventListener('resize', handleResize)
    handleResize()
    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [isMobile])

  const sortableHelperClass = `${tableName}-dragging-table-column`

  const onSortStart = () => {
    document.querySelector(`.${sortableHelperClass}`).style.zIndex = 1000
  }

  const onSortEnd = ({ oldIndex, newIndex }) => {
    moveColumn(oldIndex, newIndex)
  }

  const onSortMove = () => {
    const element = document.querySelector(`.${sortableHelperClass}`)
    const transform = window
      .getComputedStyle(element)
      .getPropertyValue('transform')
    element.style.transform = `${transform} scale(1.1)`
  }

  const sort = (newKey, direction) => {
    if (direction) {
      sortTable(newKey, direction)
      return
    }

    let newSortDirection = 'asc'

    if (Array.isArray(newKey) && isEqual(newKey, sortKey)) {
      newSortDirection = sortDirection === 'asc' ? 'desc' : 'asc'
    } else if (newKey === sortKey) {
      newSortDirection = sortDirection === 'asc' ? 'desc' : 'asc'
    }

    sortTable(newKey, newSortDirection)
  }

  const handleSearch = query => {
    if (query !== search) {
      searchTable(query)
    }
  }

  const onCheck = value => {
    check(castArray(value))
  }

  const onCheckAll = (values = [], uncheckAll = false) => {
    const { key } = columns.find(c => c.checkbox)

    const dataValues = data.map(x => x[key])

    if (uncheckAll) {
      clearChecked([])
      return
    }

    if (values.length) {
      clearChecked()
      check(values)
      return
    }

    const isAllChecked = dataValues.every(x => checked.includes(x))

    check(
      isAllChecked ? dataValues : dataValues.filter(x => !checked.includes(x)),
    )
  }

  const tableHasCheckbox = columns.some(c => c.checkbox)

  // Assume that the table is still loading, if columns haven't been parsed yet
  const segmentLoading = (!columns.length || loadingTable) && !loadingMore

  if (
    (!bulk && !forceCheckbox) ||
    !tableHasCheckbox ||
    !hasPermissions(
      flattenDeep((bulk || []).map(action => get(action, 'requiredRoles', []))),
    )
  ) {
    remove(columns, column => column.key === 'id')
  }

  const Header = (
    <TableHeader
      axis="x"
      lockAxis="x"
      helperClass={sortableHelperClass}
      pressDelay={200}
      sortKey={sortKey}
      sortDirection={sortDirection}
      tableName={tableName}
      total={total}
      data={data}
      columns={columns}
      sortable={fixed}
      search={search}
      filters={filters}
      addFilter={addFilter}
      removeFilter={removeFilter}
      invertFilter={invertFilter}
      toggleFilterMode={toggleFilterMode}
      sort={sort}
      onCheckAll={onCheckAll}
      onSortMove={onSortMove}
      onSortStart={onSortStart}
      onSortEnd={onSortEnd}
      t={t}
      isMobile={isMobile}
      graphileFilters={graphileFilters}
      stickyHeader={stickyHeader}
      stickyHeaderNoOffset={stickyHeaderNoOffset}
    />
  )

  return (
    <div style={style}>
      <GlobalStyle />

      {searchable && (
        <Search initialValue={search} handleSubmit={handleSearch} />
      )}

      {((showTotal && total >= 0) || filters?.length > 1) && (
        <FlexRow noMargin justifyContent="space-between">
          {showTotal && total >= 0 && (
            <Total
              total={total}
              totalCountText={totalCountText}
              customDataCount={customDataCount}
              data={data}
              t={t}
            />
          )}

          {filters?.length > 1 && (
            <FilterMode>
              <FlexRow>
                <FilterModeSelection
                  active={matchAllColumns}
                  onClick={() => toggleMultiColumnFilterMode(true)}>
                  <Text.H3>{t('Match all')}</Text.H3>
                  <Text.Span grey light>
                    {t('Entries must match all filtered columns')}
                  </Text.Span>
                </FilterModeSelection>
                <FilterModeSelection
                  active={!matchAllColumns}
                  onClick={() => toggleMultiColumnFilterMode(false)}>
                  <Text.H3>{t('Match any')}</Text.H3>
                  <Text.Span grey light>
                    {t('Entries must match at least one filtered column')}
                  </Text.Span>
                </FilterModeSelection>
              </FlexRow>
              <FlexRow noMargin justifyContent="center">
                <Button
                  variant="outlined"
                  onClick={() => removeAllFilters()}
                  content={t('Remove all filters')}
                />
              </FlexRow>
            </FilterMode>
          )}
        </FlexRow>
      )}

      <TableContent
        fixed={fixed}
        basic={basic}
        layout={layout}
        isMobile={isMobile}
        Header={Header}
        error={error}
        columns={columns}
        data={data}
        clickable={clickable}
        search={search}
        checked={checked}
        segmentLoading={segmentLoading}
        loadingMore={loadingMore}
        onCheck={onCheck}
        onCheckAll={onCheckAll}
        t={t}
        fallbackText={fallbackText}
      />

      {bulk && tableHasCheckbox && (
        <BulkMenu
          fixed={fixed}
          disabled={!checked.length}
          count={checked.length}
          items={bulk.map(b => {
            // lol wtf
            if (typeof b.onClick === 'function') {
              const oldOnClick = b.onClick
              b.onClick = () =>
                oldOnClick(
                  checked,
                  data.filter(x => checked.includes(x.id)),
                )
            }
            if (b.childItems && b.childItems.length > 0) {
              const oldChildItems = b.childItems
              b.childItems = oldChildItems.map(c => {
                if (typeof c.onClick === 'function') {
                  const oldOnClick = c.onClick
                  c.onClick = () =>
                    oldOnClick(
                      checked,
                      data.filter(x => checked.includes(x.id)),
                    )
                }
                return c
              })
            }

            return b
          })}
        />
      )}
    </div>
  )
}

ComposedTable.Header = Table.Header
ComposedTable.Body = Table.Body
ComposedTable.Row = Table.Row
ComposedTable.Cell = Table.Cell
ComposedTable.HeaderCell = Table.HeaderCell

export default ComposedTable
